import { User, UserStatus } from '@repo-level/core-front';

import { PlayerBonus } from 'api/common';
import { Locale } from 'languages';
import { CurrencyCode } from './currency';
import { bonusRestrictedGroups, someGroupMember, USER_GROUP } from './user';

import { BonanzaPromoApi } from 'api/cms/bonanzaPromo';
import { isPromoAvailableDate } from './isDateRange';

export enum BonanzaStatus {
  LEVEL0 = '0%',
  LEVEL1 = '25%',
  LEVEL2 = '50%',
  LEVEL3 = '75%',
  LEVEL4 = '100%',
  // tech status, not a user group
  BONUS_ISSUED = 'Bonus_Issued',
  PROMO_LOCKED = 'Used_Bonus',
}

export const BONANZA_STORAGE = 'bonanza';

export const getUserBonanzaGroup = (
  promoPrefix: string,
  statuses?: UserStatus[]
) => {
  if (!statuses) return BonanzaStatus.LEVEL0;

  const bonusGroups = statuses.filter(
    ({ id }) => id.includes(promoPrefix) && id.includes('%')
  );

  if (!bonusGroups.length) return BonanzaStatus.LEVEL0;

  const bonusGroupId = bonusGroups[0].id;

  const isHitTheBonanzaStorage = localStorage.getItem(BONANZA_STORAGE);

  if (bonusGroupId.includes(BonanzaStatus.LEVEL4) && isHitTheBonanzaStorage)
    return BonanzaStatus.PROMO_LOCKED;

  const bonusGroupPercent = bonusGroupId.split('_').slice(-1)[0];

  const isLevel4 = bonusGroupPercent === BonanzaStatus.LEVEL4;

  if (isLevel4 && !isHitTheBonanzaStorage) return BonanzaStatus.BONUS_ISSUED;

  if (!isLevel4 && isHitTheBonanzaStorage) {
    localStorage.removeItem(BONANZA_STORAGE);
  }

  return bonusGroupPercent as BonanzaStatus;
};

export const bonanzaPromoProgressPercent: Record<BonanzaStatus, number> = {
  [BonanzaStatus.LEVEL0]: 0,
  [BonanzaStatus.LEVEL1]: 25,
  [BonanzaStatus.LEVEL2]: 50,
  [BonanzaStatus.LEVEL3]: 75,
  [BonanzaStatus.LEVEL4]: 100,
  [BonanzaStatus.BONUS_ISSUED]: 100,
  [BonanzaStatus.PROMO_LOCKED]: 100,
};

export const userRewardCurrencyMap: Record<string, CurrencyCode> = {
  [Locale.ENGLISH]: CurrencyCode.USD,
  [Locale.ENGLISH_AUSTRALIA]: CurrencyCode.AUD,
  [Locale.ENGLISH_CANADA]: CurrencyCode.CAD,
  [Locale.GERMAN]: CurrencyCode.EUR,
  [Locale.FRENCH_CANADA]: CurrencyCode.CAD,
  [Locale.FRENCH]: CurrencyCode.EUR,
  [Locale.IRISH]: CurrencyCode.EUR,
  [Locale.NORWEGIAN]: CurrencyCode.NOK,
  [Locale.ENGLISH_NEW_ZEALAND]: CurrencyCode.NZD,
};

const commonRewardMap: Record<string, number> = {
  [USER_GROUP.LOYALS]: 25,
  [USER_GROUP.GOLD]: 35,
  [USER_GROUP.PLATINUM]: 50,
  [USER_GROUP.DIAMOND]: 50,
};

const norwayRewardMap: Record<string, number> = {
  [USER_GROUP.LOYALS]: 250,
  [USER_GROUP.GOLD]: 350,
  [USER_GROUP.PLATINUM]: 500,
  [USER_GROUP.DIAMOND]: 500,
};

const bonanzaGroups = [
  USER_GROUP.LOYALS,
  USER_GROUP.GOLD,
  USER_GROUP.PLATINUM,
  USER_GROUP.DIAMOND,
];

const findBonanzaGroup = (user?: User) => {
  if (!user?.statuses) return;

  return user.statuses.find(({ id }) =>
    bonanzaGroups.includes(id as USER_GROUP)
  )?.id;
};

export const getUserReward = (locale: string, user?: User) => {
  const currency = userRewardCurrencyMap[locale];

  const userGroupId = findBonanzaGroup(user) || USER_GROUP.LOYALS;

  if (locale === Locale.NORWEGIAN) {
    return { reward: norwayRewardMap[userGroupId], currency };
  }

  return { reward: commonRewardMap[userGroupId], currency };
};

export const getBonanzaBonus = (
  bonuses: PlayerBonus[],
  promoPrefix: string
) => {
  return bonuses.find(({ title }) => title.includes(promoPrefix));
};

export const checkIsBonanzaPromoAvailable = (
  data: BonanzaPromoApi | null,
  user?: User
) => {
  if (!data || !data.start || !data.end) {
    return false;
  } else {
    return (
      !someGroupMember(bonusRestrictedGroups)(user) &&
      isPromoAvailableDate(data.start, data.end)
    );
  }
};
